import { PERMISSIONS } from "@shared/constants/permissions";
import DeleteIconSvg from "@shared/svg/delete-document.svg?react";
import type { Asset } from "@shared/types/asset";
import { AlertBox } from "@shared/ui/AlertBox";
import { AssetCard, AssetCardSkeleton } from "@shared/ui/AssetCardV2";
import Button, { BUTTON_VARIANTS } from "@shared/ui/Button";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";
import { MinusCircle } from "phosphor-react";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Waypoint } from "react-waypoint";

import { DELETE_MACHINE } from "#/src/constants";
import { registerMixpanelEvent } from "#/src/utils/_mixpanel";
import AssetRowActionsMenu from "~/components/assets/AssetRowActionsMenu";
import { ITEMS_BEFORE_PAGE, ITEMS_PER_PAGE } from "~/constants/global";
import { getAssetsScrollScope } from "~/constants/scrollRestorationRoutes";
import useScrollPersist from "~/hooks/_useScrollPersist";
import usePermissions from "~/hooks/usePermissions";
import { useDeleteAsset, useListAllOwnOemAssets } from "~/services/asset";
import { WaypointEventType } from "~/types/waypointEvent";

const AssetRow = ({
  anyFilterActive = false,
  asset,
  handleEditClick,
  handleQRCodeClick,
  handleUnassignClick,
  isAssetQuery = false,
  isHierarchyPaid = false,
  isListView = false,
  isTeamsPaid = false,
  onDeleteClick,
  onDeleteSuccess,
  showQRCode = true,
  isSharedAssetPage = false,
  onAssetClick,
  showActionsDropdown = false,
  listItemsLoaded,
  listQueryKey,
  isSelectMode = false,
  isSelectedFn = (_id: string) => false,
  onToggleItem = (_id: string) => {},
  onAssetMeta,
  anySelected = false,
  portalConnectionId = "",
  sharedAssetsOrg = "",
}: {
  anyFilterActive?: boolean;
  asset: Asset;
  handleEditClick?: (asset: Asset) => void;
  handleQRCodeClick?: (asset: Asset) => void;
  handleUnassignClick?: () => void;
  isAssetQuery?: boolean;
  isHierarchyPaid?: boolean;
  isListView?: boolean;
  isTeamsPaid?: boolean;
  onDeleteClick?: () => void;
  onDeleteSuccess?: () => void;
  showQRCode?: boolean;
  isSharedAssetPage?: boolean;
  onAssetClick?: (assetId: string, assetName: string) => void;
  showActionsDropdown?: boolean;
  listItemsLoaded?: number;
  listQueryKey?: string;
  isSelectMode?: boolean;
  isSelectedFn?: (id: string) => boolean;
  onToggleItem?: (id: string) => void;
  /** Lets the list page know childrenCount for expanded (non-root) rows. */
  onAssetMeta?: (id: string, childrenCount: number) => void;
  /** Force the selection checkbox to stay visible (set once ≥1 item selected). */
  anySelected?: boolean;
  /**
   * The portal this row is being viewed through. As on `SubAssets`: expanding a
   * row lists its children, and through a portal that listing has to go via the
   * access-gated query, which authorizes against a shared ANCESTOR rather than
   * the child's own company.
   */
  portalConnectionId?: string;
  /** The organization sharing these assets, when that is not the viewer's own. */
  sharedAssetsOrg?: string;
}) => {
  const { messages } = useIntl();
  const { removeAsset } = useDeleteAsset();
  const params = useParams<{ connectionId: string }>();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { captureMainScroll } = useScrollPersist();
  const { can, canChange, canDelete, canOpen } = usePermissions();
  // Rows also list assets on other modules' pages (a product's or location's
  // assets); without access to assets they don't link anywhere.
  const canOpenDetail = !!onAssetClick || canOpen("/app/asset-hub/my-assets");
  const allowDeletion = canDelete(PERMISSIONS.assets.delete, asset.createdBy);

  const [expandChildren, setExpandChildren] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    onAssetMeta?.(asset._id, asset.childrenCount ?? 0);
  }, [asset._id, asset.childrenCount, onAssetMeta]);

  const parentAssetId = pathname.split("/").at(-1);
  const isSharedAssetsPage = useMemo(
    () => pathname.includes("/app/asset-hub/shared"),
    [pathname],
  );
  const showUnassign = useMemo(
    () =>
      isHierarchyPaid &&
      parentAssetId &&
      asset.hierarchy?.[0]?.id === parentAssetId,
    [asset.hierarchy, isHierarchyPaid, parentAssetId],
  );

  const {
    assets: subAssets,
    assetsListLoading: subAssetsLoading,
    handleFetchMore: handleFetchMoreSubAssets,
    totalCount: subAssetsTotalCount,
  } = useListAllOwnOemAssets({
    where: {
      directParentId: asset._id,
      isSharedAsset: isSharedAssetPage,
      ...(sharedAssetsOrg ? { oemId: sharedAssetsOrg } : {}),
      // Identifies the portal to authorize against, not a filter.
      ...(portalConnectionId ? { customer: portalConnectionId } : {}),
    },
    limit: ITEMS_PER_PAGE,
    skip: 0,
    skipCondition: !expandChildren || !asset.childrenCount,
    useCustomerPortalQuery: !!portalConnectionId,
  });

  const showChildren =
    !!asset.childrenCount && !anyFilterActive && !isAssetQuery;

  const handleScrollBottom = (event: WaypointEventType) => {
    if (
      subAssets.length >= ITEMS_PER_PAGE &&
      !subAssetsLoading &&
      event?.previousPosition !== Waypoint.above
    ) {
      handleFetchMoreSubAssets({
        limit: ITEMS_PER_PAGE,
        skip: subAssets.length,
      });
    }
  };

  const handleDeleteAccept = async () => {
    try {
      setIsDeleting(true);
      await removeAsset(asset._id);
      registerMixpanelEvent(DELETE_MACHINE);
      toast(
        <Toast message={messages["hierarchy"]["assets"]["deleteSuccess"]} />,
        { closeButton: false },
      );
      setShowDeleteAlert(false);
    } catch (error: any) {
      toast(<Toast message={error.message} type={TOAST_TYPES.ERROR} />, {
        closeButton: false,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClick = () => {
    if (onAssetClick) {
      onAssetClick(asset._id, asset.name);
    } else {
      const linkPath = isSharedAssetPage
        ? `/app/asset-hub/shared/${params.connectionId}/assets/${asset._id}`
        : `/app/asset-hub/my-assets/${asset._id}`;
      captureMainScroll(getAssetsScrollScope(), {
        ...(typeof listItemsLoaded === "number" ? { listItemsLoaded } : {}),
        ...(listQueryKey ? { listQueryKey } : {}),
      });
      navigate(linkPath);
    }
  };

  const showActionsTrailing =
    (showUnassign || isListView || showActionsDropdown) &&
    can(PERMISSIONS.assets.update) &&
    !isSharedAssetsPage;

  const trailingComponent = showActionsTrailing ? (
    showActionsDropdown ? (
      <AssetRowActionsMenu
        onView={handleClick}
        onUnassign={
          showUnassign && handleUnassignClick ? handleUnassignClick : undefined
        }
      />
    ) : showUnassign || (handleUnassignClick && !handleEditClick) ? (
      <Button
        variant={BUTTON_VARIANTS.LINK}
        leadingIcon={
          <MinusCircle className="u-text-color-gray-v2-60" size={20} />
        }
        tooltipId="unassign-machine"
        tooltipText={
          messages?.facilities?.facilityDetails.machinesTab.edit.changes
            .unassignWindow.yes
        }
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation();
          handleUnassignClick?.(asset);
        }}
      />
    ) : (
      <AssetRowActionsMenu
        onEdit={
          canChange(PERMISSIONS.assets.update, asset.createdBy) &&
          handleEditClick
            ? () => handleEditClick(asset)
            : undefined
        }
        onUnassign={handleUnassignClick}
        onDelete={
          allowDeletion
            ? () => {
                onDeleteClick?.();
                setShowDeleteAlert(true);
              }
            : undefined
        }
      />
    )
  ) : null;

  const assetCardData = {
    id: asset._id,
    name: asset.name,
    serialNumber: asset.serialNumber,
    thumbnail: asset.thumbnail,
    image: asset.image,
    oem: asset.oem ? { logo: asset.oem.logo } : null,
    totalChildrenCount: asset.totalChildrenCount ?? 0,
    isQRCodeEnabled: asset.isQRCodeEnabled,
    // @ts-ignore - runtime teams have name/teamColor from GraphQL, TeamType is incomplete
    teams: asset.teams,
    template: asset.template
      ? { id: asset.template._id, title: asset.template.title }
      : null,
    hierarchy: asset.hierarchy ?? [],
    connectionName: asset.customer?.name ?? null,
  };

  // Bulk selection is only offered to roles with bulk delete; others never see the
  // checkbox. When enabled, the leading thumbnail is swapped in-place for the
  // checkbox on hover and stays a checkbox once anything on screen is selected.
  const canBulkSelect = isSelectMode && can(PERMISSIONS.data.bulkDelete);
  const selected = canBulkSelect && isSelectedFn(asset._id);

  const handleToggleSelect = () => {
    onToggleItem(asset._id);
  };

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-sm">
        <div className="flex-1 min-w-0">
          <AssetCard
            asset={assetCardData}
            showExpandIcon={
              !!asset.childrenCount && !anyFilterActive && !isAssetQuery
            }
            isExpanded={expandChildren}
            onToggleExpand={() => setExpandChildren((prev) => !prev)}
            isSearching={isAssetQuery}
            showQrCode={showQRCode}
            showTeam={isTeamsPaid}
            showConnection={isListView}
            onClick={canOpenDetail ? handleClick : undefined}
            onQrCodeClick={() => handleQRCodeClick?.(asset)}
            trailingComponent={trailingComponent}
            selectable={canBulkSelect}
            isSelected={selected}
            anySelected={anySelected}
            onToggleSelect={handleToggleSelect}
          />
        </div>
      </div>

      <AlertBox
        title={`${messages["hierarchy"]["assets"]["deleteButtonText"]}?`}
        description={`${messages["hierarchy"]["assets"]["deleteAlert"]["title"]} ${messages["hierarchy"]["assets"]["deleteAlert"]["description"]}`}
        isOpen={showDeleteAlert}
        acceptButtonText={messages?.common?.delete}
        acceptButtonVariant={BUTTON_VARIANTS.DANGER}
        cancelButtonText={messages?.common?.cancel}
        disableAcceptButton={isDeleting}
        image={<DeleteIconSvg width="130" height="130" />}
        onAccept={handleDeleteAccept}
        onCancel={() => setShowDeleteAlert(false)}
        overlay={true}
      />

      {showChildren && (
        <div
          className="grid transition-all duration-200 ease-in-out"
          style={{ gridTemplateRows: expandChildren ? "1fr" : "0fr" }}
        >
          <div className="overflow-hidden">
            <div className="mt-sm ml-sm sm:ml-lg flex">
              <div className="w-px bg-tertiary flex-shrink-0" />
              <div className="ml-sm sm:ml-lg flex flex-col w-full gap-y-sm">
                {subAssetsLoading ? (
                  <AssetCardSkeleton />
                ) : (
                  subAssets.map((subAsset: Asset, index: number) => (
                    <Fragment key={`${asset._id}-${subAsset._id}`}>
                      <AssetRow
                        key={subAsset._id}
                        asset={subAsset}
                        handleEditClick={handleEditClick}
                        handleQRCodeClick={handleQRCodeClick}
                        handleUnassignClick={handleUnassignClick}
                        isAssetQuery={isAssetQuery}
                        isListView={isListView}
                        isTeamsPaid={isTeamsPaid}
                        onDeleteClick={onDeleteClick}
                        onDeleteSuccess={onDeleteSuccess}
                        isSharedAssetPage={isSharedAssetPage}
                        showQRCode={showQRCode}
                        onAssetClick={onAssetClick}
                        showActionsDropdown={showActionsDropdown}
                        isSelectMode={isSelectMode}
                        isSelectedFn={isSelectedFn}
                        onToggleItem={onToggleItem}
                        onAssetMeta={onAssetMeta}
                        anySelected={anySelected}
                        portalConnectionId={portalConnectionId}
                        sharedAssetsOrg={sharedAssetsOrg}
                      />

                      {!subAssetsLoading &&
                        subAssetsTotalCount > subAssets.length &&
                        index === subAssets.length - ITEMS_BEFORE_PAGE && (
                          // @ts-ignore
                          <Waypoint onEnter={handleScrollBottom} />
                        )}
                    </Fragment>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssetRow;
